import type { TaskOutputWithNewlyCreated } from "@orsane/schema/tasks";
import { BuiltInAttribute } from "@orsane/schema/tasks";
import { useMemo } from "react";
import type { ColumnDef } from "./ColumnModel";
import { Row, RowGroup } from "./RowModel";

export type TableModel = {
    isGrouped: boolean;
    rows: Row[];
    rowGroups: RowGroup[];
    sortColumn: ColumnDef;
    groupColumn: ColumnDef | undefined;
};

/**
 * One shared collator instead of `String.prototype.localeCompare` per comparison.
 * `localeCompare` with no arguments constructs a collator on every call, which
 * dominates sorting once a list gets big. Constructed with no options so the
 * ordering matches what `localeCompare()` produced.
 */
const collator = new Intl.Collator();

type SortKey = string | number;

/** A row paired with its precomputed sort key (decorate-sort-undecorate). */
type KeyedRow = { row: Row; sortKey: SortKey };

function compareSortKeys(a: SortKey, b: SortKey, isAscending: boolean) {
    if (typeof a === "string" && typeof b === "string") {
        return isAscending ? collator.compare(a, b) : collator.compare(b, a);
    }
    return isAscending ? (a as number) - (b as number) : (b as number) - (a as number);
}

/**
 * Pairs each row with its sort key so `getSortValue` runs once per row rather
 * than the O(n log n) times a comparator would call it. That matters because some
 * columns build their key the expensive way — the assignee and lists columns map,
 * URI-encode and join their values on every call.
 */
function keyRows(rows: Row[], sortColumn: ColumnDef): KeyedRow[] {
    const keyed: KeyedRow[] = new Array(rows.length);
    for (let i = 0; i < rows.length; i++) {
        keyed[i] = { row: rows[i], sortKey: sortColumn.getSortValue({ row: rows[i] }) };
    }
    return keyed;
}

function undecorate(keyed: KeyedRow[]): Row[] {
    const rows: Row[] = new Array(keyed.length);
    for (let i = 0; i < keyed.length; i++) {
        rows[i] = keyed[i].row;
    }
    return rows;
}

/**
 * Sort rows by their position in the manualOrder array.
 * Tasks not in the array go to the end.
 */
function manualSortComparator(manualOrder: number[]) {
    const indexMap = new Map<number, number>();
    for (let i = 0; i < manualOrder.length; i++) {
        indexMap.set(manualOrder[i], i);
    }
    return (a: Row, b: Row) => {
        // Temp tasks (negative IDs) come last
        if (a.data.id < 0 && b.data.id >= 0) return 1;
        if (a.data.id >= 0 && b.data.id < 0) return -1;

        // Tasks in manualOrder sort by their position — this takes precedence
        // over forcedSortOrder so that drag-and-drop reordering works for
        // newly created tasks that still have forcedSortOrder set.
        const aIdx = indexMap.get(a.data.id);
        const bIdx = indexMap.get(b.data.id);
        if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
        if (aIdx !== undefined) return -1;
        if (bIdx !== undefined) return 1;

        // Fallback for tasks not in manualOrder: use forcedSortOrder (newly created tasks)
        const aHasForcedSort = a.data.forcedSortOrder !== undefined;
        const bHasForcedSort = b.data.forcedSortOrder !== undefined;
        if (aHasForcedSort && !bHasForcedSort) return 1;
        if (!aHasForcedSort && bHasForcedSort) return -1;
        if (aHasForcedSort && bHasForcedSort) return a.data.forcedSortOrder! - b.data.forcedSortOrder!;

        return 0;
    };
}

/**
 * Top-level ordering: temp tasks last, then newly created tasks (which carry a
 * forcedSortOrder) after everything else, then the column's sort key.
 */
function sortTopLevelRows(rows: Row[], sortColumn: ColumnDef, sortIsAscending: boolean): Row[] {
    const keyed = keyRows(rows, sortColumn);
    keyed.sort((a, b) => {
        // Make sure the negative ID's (temp tasks) come last (These are the new tasks)
        const aIsTemp = a.row.data.id < 0;
        const bIsTemp = b.row.data.id < 0;
        if (aIsTemp !== bIsTemp) return aIsTemp ? 1 : -1;

        // A task with forcedSortOrder sorts after one without; two of them sort
        // against each other by that value.
        const aHasForcedSort = a.row.data.forcedSortOrder !== undefined;
        const bHasForcedSort = b.row.data.forcedSortOrder !== undefined;
        if (aHasForcedSort !== bHasForcedSort) return aHasForcedSort ? 1 : -1;
        if (aHasForcedSort && bHasForcedSort) {
            return a.row.data.forcedSortOrder! - b.row.data.forcedSortOrder!;
        }

        return compareSortKeys(a.sortKey, b.sortKey, sortIsAscending);
    });
    return undecorate(keyed);
}

function sortSubtaskRows(rows: Row[], sortColumn: ColumnDef, sortIsAscending: boolean): Row[] {
    const keyed = keyRows(rows, sortColumn);
    keyed.sort((a, b) => {
        // A subtask still being typed (temp, negative ID) stays below its siblings, as at
        // the top level. Otherwise its empty title would sort it above them.
        const aIsTemp = a.row.data.id < 0;
        const bIsTemp = b.row.data.id < 0;
        if (aIsTemp !== bIsTemp) return aIsTemp ? 1 : -1;

        return compareSortKeys(a.sortKey, b.sortKey, sortIsAscending);
    });
    return undecorate(keyed);
}

/**
 * Recursively attaches subtask rows to their parent rows.
 * Builds a tree structure using the parentTask.id field on each task.
 */
function buildSubtaskTree(
    allRows: Row[],
    childrenByParentId: Map<number, TaskOutputWithNewlyCreated[]>,
    sortColumn: ColumnDef,
    sortIsAscending: boolean,
    depth: number,
    isManualSort: boolean,
    manualSubtaskOrder: Record<number, number[]>
) {
    for (const row of allRows) {
        const children = childrenByParentId.get(row.data.id);
        if (children && children.length > 0) {
            // Key is stable regardless of depth (task.key is unique per task). Keeping it
            // stable across a top-level <-> subtask transition means React reuses the same
            // DOM node instead of remounting — which preserves focus / in-progress text of
            // a title cell being edited while it is (un)nested via Tab.
            let childRows = children.map((task) => new Row(`row_${task.key}`, task, depth));

            if (isManualSort) {
                const parentOrder = manualSubtaskOrder[row.data.id] || [];
                childRows.sort(manualSortComparator(parentOrder));
            } else {
                childRows = sortSubtaskRows(childRows, sortColumn, sortIsAscending);
            }

            row.subtaskRows = childRows;
            row.hasSubtasks = true;
            buildSubtaskTree(
                childRows,
                childrenByParentId,
                sortColumn,
                sortIsAscending,
                depth + 1,
                isManualSort,
                manualSubtaskOrder
            );
        }
    }
}

export type BuildTableModelParams = {
    columns: ColumnDef[];
    sortColumnId: string;
    sortIsAscending: boolean;
    groupColumnId: string | null;
    groupSortIsAscending: boolean;
    rawTasks: TaskOutputWithNewlyCreated[];
    manualOrder: number[];
    manualSubtaskOrder: Record<number, number[]>;
};

/**
 * Pure model builder behind `useTableModel`. Exported so it can be measured
 * directly (see bench/model.bench.ts) without a React render.
 */
export function buildTableModel({
    columns,
    sortColumnId,
    sortIsAscending,
    groupColumnId,
    groupSortIsAscending,
    rawTasks,
    manualOrder,
    manualSubtaskOrder,
}: BuildTableModelParams): TableModel {
    const isGrouped = groupColumnId !== null;
    const isManualSort = sortColumnId === BuiltInAttribute.manual;

    // Separate top-level tasks from subtasks.
    // A task is only nested if its parent is also present in the current list.
    // Deduplicate by task ID — the same subtask can appear from both the main
    // query (when it matches filters) and from fetchDescendants.
    const taskIds = new Set<number>();
    const allTasks: TaskOutputWithNewlyCreated[] = [];
    for (const task of rawTasks ?? []) {
        if (!taskIds.has(task.id)) {
            taskIds.add(task.id);
            allTasks.push(task);
        }
    }

    const topLevelTasks: TaskOutputWithNewlyCreated[] = [];
    const childrenByParentId = new Map<number, TaskOutputWithNewlyCreated[]>();

    for (const task of allTasks) {
        // Nest under parent when the parent is in the result set
        if (task.parentTask && taskIds.has(task.parentTask.id)) {
            const parentId = task.parentTask.id;
            const siblings = childrenByParentId.get(parentId);
            if (siblings) {
                siblings.push(task);
            } else {
                childrenByParentId.set(parentId, [task]);
            }
        } else {
            // Only show as top-level if the task has no parent in the current list
            topLevelTasks.push(task);
        }
    }

    // Create rows from top-level tasks only
    let rows: Row[] = topLevelTasks.map((task) => new Row(`row_${task.key}`, task, 0));

    const sortColumn = columns.find((column) => column.id === sortColumnId);
    if (!sortColumn) {
        throw new Error(`Sort column ${sortColumnId} not found`);
    }

    if (isManualSort) {
        rows.sort(manualSortComparator(manualOrder));
    } else {
        rows = sortTopLevelRows(rows, sortColumn, sortIsAscending);
    }

    // Build subtask tree: attach subtask rows to their parents recursively
    buildSubtaskTree(rows, childrenByParentId, sortColumn, sortIsAscending, 1, isManualSort, manualSubtaskOrder);

    // group by the group column and put in structure with the classes Row / RowGroup
    const groupedRows: RowGroup[] = [];
    let groupColumn: ColumnDef | undefined;
    if (isGrouped) {
        groupColumn = columns.find((col) => col.id === groupColumnId);
        if (groupColumn === undefined) {
            throw new Error(`Group column ${groupColumnId} not found`);
        }

        // Keyed by grouped value so assigning rows to groups stays linear. Scanning
        // the groups array per row is O(rows * groups), which bites as soon as the
        // group column is high-cardinality (assignee, lists, due date).
        const groupsByValue = new Map<SortKey, RowGroup>();
        for (const row of rows) {
            const groupedValue = groupColumn.getSortValue({ row });
            const group = groupsByValue.get(groupedValue);
            if (group) {
                row.group = group;
                group.rows.push(row);
            } else {
                const newGroup = new RowGroup(`group_${groupColumnId}_${row.key}`, [row], groupedValue);
                row.group = newGroup;
                groupsByValue.set(groupedValue, newGroup);
                groupedRows.push(newGroup);
            }
        }

        // sort the groups by groupedValue
        groupedRows.sort((a, b) => compareSortKeys(a.groupedValue, b.groupedValue, groupSortIsAscending));
    }

    return {
        isGrouped: isGrouped,
        rows: rows,
        rowGroups: groupedRows,
        sortColumn: sortColumn,
        groupColumn: groupColumn,
    };
}

export function useTableModel(
    columns: ColumnDef[],
    sortColumnId: string,
    sortIsAscending: boolean,
    groupColumnId: string | null,
    groupSortIsAscending: boolean,
    rawTasks: TaskOutputWithNewlyCreated[],
    manualOrder: number[],
    manualSubtaskOrder: Record<number, number[]>
): TableModel {
    return useMemo(
        () =>
            buildTableModel({
                columns,
                sortColumnId,
                sortIsAscending,
                groupColumnId,
                groupSortIsAscending,
                rawTasks,
                manualOrder,
                manualSubtaskOrder,
            }),
        [
            columns,
            sortColumnId,
            sortIsAscending,
            groupColumnId,
            groupSortIsAscending,
            rawTasks,
            manualOrder,
            manualSubtaskOrder,
        ]
    );
}
